import React, { useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import { InternalStandardProps } from "@mui/material";
import { Layout } from "../Layout";
import {
    useGlobalGifts,
    useProspectingUser,
    useReceivedMemberVideoGifts,
    useReceivedProspectVideoGifts,
} from "JS/React/Hooks/Gifts";
import { useGlobalVideos, useVideos } from "JS/React/Hooks/Video";
import { AppHeader } from "JS/React/Components/AppHeader";
import { VideoCategoryList } from "./Components/VideoCategoryList";
import { uniqBy } from "lodash-es";
import { messaging } from "JS/Helpers/UserMessaging";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { useGlobalNavStack } from "JS/React/Hooks/NavStack";
import { useGetFavorites, useGlobalFavorites } from "JS/React/Hooks/Favorites";
import { useAttributes, useGlobalAttributes } from "JS/React/Hooks/Attributes";
import { AttributeType } from "JS/Redux/Attributes";
import { Content, ReceivedGift } from "JS/Models";
import { MultiAutocompleteProps } from "JS/React/Components/AppMultiTagSelect";
import { useScrollWithDelay } from "JS/React/Hooks/Media";
import { useGlobalMediaEssentials } from "JS/React/Hooks/MediaEssentials";
import { ListingComponent } from "../Audios/ListingComponent";
import { useVideoResumeHelper } from "./VideoHelpers";
import { useVideosHandlers } from "./VideoHooks";
import { useDownloadedContentStatus } from "./DownloadedContentStatus";
import { ContentAttribute } from "JS/Models/Attributes";

const Root = styled("div")(({ theme }) => ({
    padding: theme.spacing(1),
}));

export interface VideosProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {}

export enum VideoCategoryTitle {
    ALL_VIDEOS = "all-videos",
    MY_VIDEOS = "my-videos",
    SAVED = "saved-videos",
    RECENTLY_PLAYED = "recently_played",
    INDEX = "index",
}

export const Videos = (props: VideosProps) => {
    const { className, ...rest } = props;

    const { videos, categories: categorizedVideos } = useGlobalVideos();

    const { favNids } = useGlobalFavorites();

    useGetFavorites(!!favNids?.length);
    const { isResumable } = useVideoResumeHelper();
    const { receivedMemberVideos, receivedProspectVideos } = useGlobalGifts();
    const { isProsUser } = useProspectingUser();
    const { loading: videosLoading } = useVideos();

    useReceivedMemberVideoGifts();
    useReceivedProspectVideoGifts(!isProsUser);
    const { setGlobalVideosStack, resetGlobalVideosStack, videosStack } =
        useGlobalNavStack();

    const [searchQuery, setSearchQuery] = useState(videosStack?.searchQuery);
    const {
        mediaEssentials,
        videoUniqueSpeakers,
        videoSelectedSpeakers,
        attributesLoading,
        onToggleSpeakerSelect,
        handleClearFilters,
        handleResetVideoAttributes,
    } = useVideoAttributesHandlers();

    const [showList, setShowList] = useState(false);

    const filteredVideos = useMemo(() => {
        return uniqBy(
            [
                ...videos,
                ...(receivedMemberVideos || []),
                ...(receivedProspectVideos || []),
            ],
            (x) => x.nid,
        );
    }, [videos, receivedMemberVideos, receivedProspectVideos]);
    const { handleListItemClick } = useVideosHandlers(
        searchQuery,
        VideoCategoryTitle.INDEX,
    );
    const { searchResults } = useFilterVideoHandlers(
        filteredVideos,
        searchQuery,
        videoSelectedSpeakers,
        videoUniqueSpeakers,
    );

    useEffect(() => {
        setGlobalVideosStack({
            searchQuery: searchQuery,
        });
    }, [searchResults, videoSelectedSpeakers]);
    const loaded = !videosLoading && !attributesLoading;
    const useVideosDownloadHelperData = (data: (Content | ReceivedGift)[]) => {
        const { getDownloadedStatusById } = useDownloadedContentStatus(
            data,
            "video",
        );
        return getDownloadedStatusById;
    };
    const videoListVisible =
        showList ||
        searchQuery?.length > 0 ||
        videoSelectedSpeakers?.length > 0;

    useScrollWithDelay();

    return (
        <Layout>
            <Root className={className} {...rest}>
                <AppHeader
                    title="Videos"
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    searchPlaceholder={"Search Videos by title/description"}
                    onSearchFieldClick={() => {
                        setShowList(true);
                    }}
                    onBackClick={() => {
                        setSearchQuery("");
                        resetGlobalVideosStack();
                        handleResetVideoAttributes();
                        setShowList(false);
                    }}
                    canGoBack={videoListVisible}
                    filterable={mediaEssentials?.allow_speaker_feature}
                    onToggleSpeakerSelect={onToggleSpeakerSelect}
                    uniqueSpeakers={videoUniqueSpeakers}
                    selectedSpeakers={videoSelectedSpeakers}
                    handleClearFilters={handleClearFilters}
                />

                {loaded ? (
                    <>
                        {videoListVisible ? (
                            <ListingComponent
                                listContent={searchResults}
                                searchQuery={searchQuery}
                                categoryId={VideoCategoryTitle.INDEX}
                                showSortHeader={true}
                                setGlobalStack={setGlobalVideosStack}
                                globalStack={videosStack}
                                isResumeable={isResumable}
                                favoritesContent={favNids}
                                handleListItemClick={handleListItemClick}
                                getDownloadHelperData={
                                    useVideosDownloadHelperData
                                }
                                controlType="video"
                            />
                        ) : (
                            <VideoCategoryList
                                videos={videos}
                                categorizedVideos={categorizedVideos}
                            />
                        )}
                    </>
                ) : (
                    <AppBackdropProgress
                        open={true}
                        backdropText={
                            !attributesLoading
                                ? messaging.loader.videos
                                : messaging.loader.fectchingSpeakers
                        }
                    />
                )}
            </Root>
        </Layout>
    );
};

export const useVideoAttributesHandlers = () => {
    const {
        videoFetchStatus,
        videoUniqueSpeakers,
        videoSelectedSpeakers,
        setVideoSelectedSpeakers,
        resetVideoSelectedSpeakers,
    } = useGlobalAttributes();

    const { mediaEssentials } = useGlobalMediaEssentials();

    const { loading: attributesLoading } = useAttributes(
        AttributeType.VIDEO,
        videoFetchStatus || !mediaEssentials?.allow_speaker_feature,
    );

    const onToggleSpeakerSelect: MultiAutocompleteProps["onChange"] = (
        _,
        value,
    ) => {
        setVideoSelectedSpeakers(value);
    };

    const handleClearFilters = () => resetVideoSelectedSpeakers();
    const handleResetVideoAttributes = () => resetVideoSelectedSpeakers();

    return {
        mediaEssentials,
        videoUniqueSpeakers,
        videoSelectedSpeakers,
        attributesLoading,
        onToggleSpeakerSelect,
        handleClearFilters,
        handleResetVideoAttributes,
    };
};

export const useFilterVideoHandlers = (
    filteredVideos: (Content | ReceivedGift)[],
    searchQuery: string,
    videoSelectedSpeakers: string[],
    videoContentSpeakers: ContentAttribute[],
) => {
    const filterNids = useMemo(() => {
        return videoContentSpeakers
            ? videoContentSpeakers.filter((x) =>
                  x.attributeValues.some((z) =>
                      videoSelectedSpeakers.includes(z),
                  ),
              )
            : [];
    }, [videoSelectedSpeakers, videoContentSpeakers]);

    const filteredResults = useMemo(() => {
        return filterNids.length > 0
            ? filteredVideos?.filter((d) =>
                  filterNids.some((z) => z.nid.toString() === d.nid),
              )
            : filteredVideos;
    }, [filteredVideos, filterNids]);

    const searchResults = useMemo(() => {
        return filteredResults?.filter(
            (d) =>
                d.title
                    .toLocaleLowerCase()
                    .includes(searchQuery.toLocaleLowerCase().trim()) ||
                d.description
                    .toLocaleLowerCase()
                    .includes(searchQuery.toLocaleLowerCase().trim()),
        );
    }, [filteredResults, searchQuery]);

    return { searchResults };
};
